import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, InputNumber, Modal } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { bpToPercent, changeShare, percentToBp, type Stakeholder } from './stakeholderApi'

interface ShareChangeModalProps {
  stakeholder: Stakeholder | null
  onClose: () => void
}

interface FormValues {
  sharePercent: number
  effectiveFrom: Dayjs
  reason: string
}

/** Ulush tarixga yangi qator bo'lib yoziladi; doiradagi jami 100% dan oshsa server rad etadi. */
export function ShareChangeModal({ stakeholder, onClose }: ShareChangeModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    if (!stakeholder) return
    form.setFieldsValue({
      sharePercent: bpToPercent(stakeholder.summary.ownershipBp),
      effectiveFrom: tenantToday(),
      reason: '',
    })
  }, [stakeholder, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      if (!stakeholder) throw new Error('stakeholder')
      return changeShare(stakeholder.id, {
        shareBp: percentToBp(values.sharePercent),
        effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD'),
        reason: values.reason.trim(),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={stakeholder !== null}
      title={t('stakeholder.share.title')}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      destroyOnHidden
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('stakeholder.share.note')} />
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="sharePercent"
          label={t('stakeholder.field.share')}
          rules={[{ required: true, message: t('stakeholder.validation.share') }]}
        >
          <InputNumber min={0} max={100} step={0.01} precision={2} addonAfter="%" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="effectiveFrom"
          label={t('stakeholder.share.effectiveFrom')}
          rules={[{ required: true, message: t('stakeholder.validation.effectiveFrom') }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="reason"
          label={t('actions.reason')}
          rules={[{ required: true, whitespace: true, message: t('stakeholder.validation.reason') }]}
        >
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
